import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API } from '../../App';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { toast } from 'sonner';
import { Wand2, ArrowRight, ArrowLeft, Check } from 'lucide-react';

const METERS_TO_FEET = 3.28084;

export default function OrganizerExhibitionWizard() {
  const { exhibitionId } = useParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [exhibition, setExhibition] = useState(null);
  const [hallCount, setHallCount] = useState(1);
  const [currentHallIndex, setCurrentHallIndex] = useState(0);
  const [imperialMode, setImperialMode] = useState(false);
  const [halls, setHalls] = useState([]);

  useEffect(() => {
    fetchExhibition();
  }, [exhibitionId]);

  const fetchExhibition = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/exhibitions/${exhibitionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExhibition(res.data);
    } catch (error) {
      toast.error('Fuar yüklenemedi');
    }
  };

  const handleHallCountSubmit = () => {
    if (hallCount < 1) {
      toast.error('En az 1 hol olmalı');
      return;
    }
    // Initialize halls array with empty values
    const initialHalls = Array.from({ length: hallCount }, (_, i) => ({
      name: `${i + 1}. Hol`,
      width: '',
      length: '',
      widthDisplay: '',
      lengthDisplay: ''
    }));
    setHalls(initialHalls);
    setStep(2);
  };

  const handleHallDataChange = (field, value) => {
    const newHalls = [...halls];
    newHalls[currentHallIndex][field] = value;
    
    // Auto-update display values for preview
    newHalls[currentHallIndex].widthDisplay = newHalls[currentHallIndex].width;
    newHalls[currentHallIndex].lengthDisplay = newHalls[currentHallIndex].length;
    
    setHalls(newHalls);
  };

  const toggleImperial = () => {
    const newMode = !imperialMode;
    setImperialMode(newMode);
    
    // Convert all halls
    const converted = halls.map(hall => ({
      ...hall,
      widthDisplay: newMode ? hall.width * METERS_TO_FEET : hall.width / METERS_TO_FEET,
      lengthDisplay: newMode ? hall.length * METERS_TO_FEET : hall.length / METERS_TO_FEET,
    }));
    setHalls(converted);
  };

  const handleNextHall = () => {
    if (currentHallIndex < halls.length - 1) {
      setCurrentHallIndex(currentHallIndex + 1);
    } else {
      setStep(3);
    }
  };

  const handlePrevHall = () => {
    if (currentHallIndex > 0) {
      setCurrentHallIndex(currentHallIndex - 1);
    }
  };

  const handleFinish = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Save all halls
      for (let i = 0; i < halls.length; i++) {
        const hall = halls[i];
        const widthMeters = imperialMode ? hall.width / METERS_TO_FEET : hall.width;
        const lengthMeters = imperialMode ? hall.length / METERS_TO_FEET : hall.length;
        
        await axios.post(`${API}/halls`, {
          exhibition_id: exhibitionId,
          name: hall.name,
          width_meters: widthMeters,
          length_meters: lengthMeters,
          order: i
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      toast.success('Holler başarıyla oluşturuldu!');
      navigate(`/organizer/edit/${exhibitionId}`);
    } catch (error) {
      toast.error('Holler kaydedilemedi');
    }
  };

  const currentHall = halls[currentHallIndex];
  const areaDisplay = currentHall && currentHall.width && currentHall.length ? 
    (imperialMode ? 
      (currentHall.width * currentHall.length / (METERS_TO_FEET * METERS_TO_FEET)).toFixed(2) : 
      (currentHall.width * currentHall.length).toFixed(2)
    ) : 0;

  if (!exhibition) {
    return <div className="p-8">Yükleniyor...</div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Wand2 className="w-8 h-8 text-teal-600" />
            <h1 className="text-4xl font-bold text-gray-900">Fuar Sihirbazı</h1>
          </div>
          <p className="text-gray-600">{exhibition.name}</p>
          <div className="mt-4 flex items-center justify-center gap-2">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-teal-600 text-white' : 'bg-gray-200'}`}>
              {step > 1 ? <Check className="w-5 h-5" /> : '1'}
            </div>
            <div className={`h-1 w-16 ${step >= 2 ? 'bg-teal-600' : 'bg-gray-200'}`} />
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-teal-600 text-white' : 'bg-gray-200'}`}>
              {step > 2 ? <Check className="w-5 h-5" /> : '2'}
            </div>
            <div className={`h-1 w-16 ${step >= 3 ? 'bg-teal-600' : 'bg-gray-200'}`} />
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 3 ? 'bg-teal-600 text-white' : 'bg-gray-200'}`}>
              3
            </div>
          </div>
        </div>

        {/* Step 1: Hall Count */}
        {step === 1 && (
          <Card>
            <CardHeader>
              <CardTitle>Kaç Tane Hol Var?</CardTitle>
              <CardDescription>Fuar alanınızdaki toplam hol sayısını girin</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="hallCount">Hol Sayısı</Label>
                <Input
                  id="hallCount"
                  type="number"
                  min="1"
                  value={hallCount}
                  onChange={(e) => setHallCount(parseInt(e.target.value) || 1)}
                  className="text-2xl text-center font-bold max-w-xs mx-auto"
                  data-testid="hall-count-input"
                />
              </div>
              
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="imperial"
                  checked={imperialMode}
                  onCheckedChange={setImperialMode}
                  data-testid="imperial-checkbox"
                />
                <Label htmlFor="imperial" className="cursor-pointer">
                  Imperial Sistem Kullan (Feet/Sq.Ft)
                </Label>
              </div>

              <Button 
                onClick={handleHallCountSubmit} 
                className="w-full bg-teal-600 hover:bg-teal-700"
                data-testid="next-to-hall-details"
              >
                Devam Et
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Hall Details */}
        {step === 2 && currentHall && (
          <Card>
            <CardHeader>
              <CardTitle>Hol {currentHallIndex + 1} / {halls.length}</CardTitle>
              <CardDescription>Hol bilgilerini girin</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label>Hol Adı/Numarası</Label>
                <Input
                  value={currentHall.name}
                  onChange={(e) => handleHallDataChange('name', e.target.value)}
                  placeholder="Lütfen bu salon adını giriniz"
                  data-testid="hall-name-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>En ({imperialMode ? 'feet' : 'metre'})</Label>
                  <Input
                    type="number"
                    value={currentHall.width}
                    onChange={(e) => handleHallDataChange('width', e.target.value === '' ? '' : parseFloat(e.target.value))}
                    placeholder="0"
                    data-testid="hall-width-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Boy ({imperialMode ? 'feet' : 'metre'})</Label>
                  <Input
                    type="number"
                    value={currentHall.length}
                    onChange={(e) => handleHallDataChange('length', e.target.value === '' ? '' : parseFloat(e.target.value))}
                    placeholder="0"
                    data-testid="hall-length-input"
                  />
                </div>
              </div>

              {/* Preview */}
              <div className="border-2 border-gray-300 rounded-lg p-6 bg-white">
                <div className="text-center mb-4">
                  <h3 className="font-semibold text-lg">{currentHall.name}</h3>
                  <p className="text-sm text-gray-600">
                    Alan: {areaDisplay} {imperialMode ? 'sq.ft' : 'm²'}
                  </p>
                </div>
                <div className="flex items-center justify-center">
                  {currentHall.width && currentHall.length ? (
                    <div 
                      className="border-4 border-gray-400 bg-gray-50 flex items-center justify-center"
                      style={{
                        width: `${Math.min(currentHall.width * 1.5, 300)}px`,
                        height: `${Math.min(currentHall.length * 1.5, 300)}px`,
                      }}
                    >
                      <div className="text-center text-gray-600">
                        <div className="font-semibold">{currentHall.width} × {currentHall.length}</div>
                        <div className="text-sm">{imperialMode ? 'feet' : 'metre'}</div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-gray-400 py-12">
                      Ölçüleri girdikten sonra önizleme görünecek
                    </div>
                  )}
                </div>
              </div>

              <div className="flex gap-4">
                {currentHallIndex > 0 && (
                  <Button 
                    variant="outline"
                    onClick={handlePrevHall}
                    data-testid="prev-hall-btn"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Önceki
                  </Button>
                )}
                <Button 
                  onClick={handleNextHall}
                  className="flex-1 bg-teal-600 hover:bg-teal-700"
                  data-testid="next-hall-btn"
                >
                  {currentHallIndex < halls.length - 1 ? 'Sonraki Salon Bilgilerini Giriniz' : 'Önizleme'}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Preview All */}
        {step === 3 && (
          <Card>
            <CardHeader>
              <CardTitle>Tüm Holler - Önizleme</CardTitle>
              <CardDescription>Hollerinizi kontrol edin ve onaylayın</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                {halls.map((hall, index) => {
                  const area = imperialMode ? 
                    (hall.width * hall.length / (METERS_TO_FEET * METERS_TO_FEET)).toFixed(2) : 
                    (hall.width * hall.length).toFixed(2);
                  
                  return (
                    <div key={index} className="border-2 border-gray-300 rounded-lg p-4 bg-white">
                      <h3 className="font-semibold text-lg mb-2">{hall.name}</h3>
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <span className="text-gray-600">En:</span> {hall.width} {imperialMode ? 'feet' : 'm'}
                        </div>
                        <div>
                          <span className="text-gray-600">Boy:</span> {hall.length} {imperialMode ? 'feet' : 'm'}
                        </div>
                        <div>
                          <span className="text-gray-600">Alan:</span> {area} {imperialMode ? 'sq.ft' : 'm²'}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="flex gap-4">
                <Button 
                  variant="outline"
                  onClick={() => {
                    setStep(2);
                    setCurrentHallIndex(0);
                  }}
                  data-testid="back-to-edit-btn"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Düzenle
                </Button>
                <Button 
                  onClick={handleFinish}
                  className="flex-1 bg-teal-600 hover:bg-teal-700"
                  data-testid="finish-wizard-btn"
                >
                  <Check className="w-4 h-4 mr-2" />
                  Tamamla ve Kroki Editörüne Geç
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
